// Role-based access control (RBAC) — central whitelist of route paths per role.
// Admin roles bypass all checks (they see everything). Clinical/operational roles
// see only the modules and routes relevant to their scope.
//
// The check is used in TWO places:
//   1. src/router/index.js — beforeEach guard that redirects unauthorized paths → /dashboard
//   2. src/components/Dashboard.vue — module tile filter (hide what they can't open)
//
// Design notes:
//   - Missing role in ROLE_ROUTES = fail-open (unknown role gets everything).
//     Reason: prevents a fresh custom role from locking a user out of the whole app;
//     admins add specific scopes explicitly.
//   - Universal routes (/dashboard, /profile, /change-password, /security, /login)
//     are appended to every role automatically so self-service always works.

export const ADMIN_ROLES = [
  'super_admin', 'hospital_admin', 'platform_admin',
  'craft_care_support', 'org_admin', 'branch_manager'
];

// Always allowed for any signed-in user (self-service + landing).
const UNIVERSAL = [
  '/dashboard', '/profile', '/change-password', '/security',
  '/notifications', '/consent'  // patient-consent panel is universal so consent-requesters work
];

export const ROLE_ROUTES = {
  triage_001: ['/triage', '/patients', '/patients/:patientId/chart', '/queue', '/vitals'],
  triage_02:  ['/triage', '/patients', '/patients/:patientId/chart', '/queue', '/vitals'],

  doctor: [
    '/consultation', '/patients', '/patients/:patientId/chart', '/queue',
    '/vitals', '/lab', '/radiology', '/mar', '/admissions', '/wards',
    '/ai', '/telehealth', '/referrals', '/appointments',
    '/timeline', '/registry', '/art', '/tb', '/mental-health', '/sgbv', '/maternity',
    '/department-overview'
  ],

  consultant: [
    '/consultation', '/patients', '/patients/:patientId/chart', '/queue',
    '/vitals', '/lab', '/radiology', '/mar', '/admissions', '/wards',
    '/ai', '/telehealth', '/referrals', '/appointments',
    '/timeline', '/registry', '/art', '/tb', '/mental-health', '/sgbv', '/maternity',
    '/department-overview'
  ],

  clinical_officer: [
    '/consultation', '/triage', '/patients', '/patients/:patientId/chart', '/queue',
    '/vitals', '/lab', '/radiology', '/mar', '/admissions',
    '/ai', '/referrals', '/appointments', '/registry'
  ],

  nurse: [
    '/triage', '/vitals', '/mar', '/admissions', '/wards',
    '/patients', '/patients/:patientId/chart', '/queue', '/maternity',
    '/immunization', '/appointments'
  ],

  pharmacist: [
    '/pharmacy', '/patients', '/patients/:patientId/chart',
    '/queue', '/stores', '/mar'
  ],

  lab_technician: [
    '/lab', '/lab/results', '/patients', '/patients/:patientId/chart', '/queue'
  ],

  radiologist: [
    '/radiology', '/patients', '/patients/:patientId/chart', '/queue'
  ],

  receptionist: [
    '/patients', '/patients/:patientId/chart', '/queue', '/appointments'
  ],

  billing_clerk: [
    '/billing', '/insurance', '/insurance-rates', '/sha',
    '/patients', '/patients/:patientId/chart', '/queue'
  ],

  housekeeping: [
    '/housekeeping', '/wards'
  ],

  morgue_attendant: [
    '/funeral-home', '/mortality'
  ]
};

// Compile role → RegExp[] for fast matching (turns "/patients/:patientId/chart"
// into ^/patients/[^/]+/chart$).
const routePatternCache = {};
function patternsFor(role) {
  if (routePatternCache[role]) return routePatternCache[role];
  const list = (ROLE_ROUTES[role] || []).concat(UNIVERSAL);
  const patterns = list.map(function (r) {
    return new RegExp('^' + r.replace(/:\w+/g, '[^/]+') + '$');
  });
  routePatternCache[role] = patterns;
  return patterns;
}

export function isAdminRole(roleCode) {
  return !!roleCode && ADMIN_ROLES.indexOf(roleCode) !== -1;
}

// Custom per-role module list stored on the roles table (roles.allowed_modules,
// CSV of route paths). Login.vue stashes it in sessionStorage at sign-in.
// When present it OVERRIDES the hardcoded ROLE_ROUTES whitelist, so admin-defined
// custom roles are honored end-to-end. Empty/missing = fall back to ROLE_ROUTES.
const customPatternCache = {};
function customPatterns() {
  let csv = '';
  try { csv = sessionStorage.getItem('allowedModules') || ''; } catch (e) { csv = ''; }
  if (!csv) return null;
  if (customPatternCache[csv]) return customPatternCache[csv];
  const list = csv.split(',').map(function (s) { return s.trim(); }).filter(Boolean).concat(UNIVERSAL);
  const patterns = list.map(function (r) {
    return new RegExp('^' + r.replace(/:\w+/g, '[^/]+') + '$');
  });
  customPatternCache[csv] = patterns;
  return patterns;
}

export function canAccessRoute(roleCode, routePath) {
  if (!roleCode) return false;
  if (isAdminRole(roleCode)) return true;
  // Admin-defined module list on the role wins over the built-in whitelist
  const custom = customPatterns();
  if (custom) return custom.some(function (rx) { return rx.test(routePath); });
  // Unknown role = fail-open (see design notes above)
  if (!ROLE_ROUTES[roleCode]) return true;
  // Also allow the always-allowed universal routes
  return patternsFor(roleCode).some(function (rx) { return rx.test(routePath); });
}

// Helper used by dashboard tile filter — same rules as canAccessRoute.
export function canAccessModule(roleCode, moduleRoute) {
  return canAccessRoute(roleCode, moduleRoute);
}

// Current-user helper — reads the role_code that Login.vue stashes in sessionStorage.
export function currentUserRole() {
  try {
    return sessionStorage.getItem('userRole') || null;
  } catch (e) { return null; }
}
